import { api } from './api';

/**
 * Authentication Service
 * Handles all authentication-related API calls and token management
 */
const authService = {
  /**
   * Login with username/email and password
   * @param {string} identifier - Username or email
   * @param {string} password - User password
   * @returns {Promise} - Promise with user data
   */
  login: async (identifier, password) => {
    try {
      // Backend expects email and password, so we'll send identifier as email
      console.log('Login payload:', { email: identifier, password: password ? '********' : null });
      
      const response = await api.post('/api/auth/login', { email: identifier, password });
      console.log('Login response:', response.data);
      
      if (response.data.data && response.data.data.token) {
        localStorage.setItem('authToken', response.data.data.token);
        localStorage.setItem('currentUser', JSON.stringify(response.data.data.user));
      }
      return response.data;
    } catch (error) {
      console.error('Login error details:', error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * Logout the current user
   */
  logout: () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('currentUser');
    // Optionally call the logout endpoint if needed
    return api.post('/api/auth/logout');
  },

  /**
   * Get the current authenticated user
   * @returns {Promise} - Promise with current user data
   */
  getCurrentUser: async () => {
    try {
      const response = await api.get('/api/auth/me');
      return response.data;
    } catch (error) {
      console.error('Get current user error:', error);
      throw error;
    }
  },

  /**
   * Update current user's password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise} - Promise with success message
   */
  updatePassword: async (currentPassword, newPassword) => {
    try {
      const response = await api.put('/api/auth/update-password', {
        currentPassword,
        newPassword
      });
      return response.data;
    } catch (error) {
      console.error('Update password error:', error);
      throw error;
    }
  },

  /**
   * Check if user is authenticated
   * @returns {boolean} - True if authenticated
   */
  isAuthenticated: () => {
    return !!localStorage.getItem('authToken');
  },

  /**
   * Get current user from localStorage
   * @returns {Object|null} - User object or null
   */
  getUser: () => {
    const user = localStorage.getItem('currentUser');
    return user ? JSON.parse(user) : null;
  },

  /**
   * Check if current user is an admin
   * @returns {boolean} - True if user is admin
   */
  isAdmin: () => {
    const user = authService.getUser();
    return user && user.role === 'admin';
  },

  /**
   * Admin: Get all users
   * @returns {Promise} - Promise with users list
   */
  getUsers: async () => {
    try {
      console.log('Calling API: GET /auth/users');
      const response = await api.get('/api/auth/users');
      console.log('Raw API response from getUsers:', response);
      
      // Check response structure
      if (response && response.data) {
        if (response.data.success && Array.isArray(response.data.data)) {
          // Backend returns {success: true, data: [...users]}
          console.log('Successful response with data array:', response.data.data);
          return response.data.data; // Return just the data array
        } else if (Array.isArray(response.data)) {
          // In case backend returns users array directly
          console.log('Successful response with direct array:', response.data);
          return response.data;
        } else {
          console.error('Unexpected response structure:', response.data);
          return []; // Return empty array as fallback
        }
      } else {
        console.error('Empty or invalid response:', response);
        return []; // Return empty array as fallback
      }
    } catch (error) {
      console.error('Get users error:', error);
      throw error;
    }
  },

  /**
   * Admin: Get a specific user by ID
   * @param {string} userId - User ID
   * @returns {Promise} - Promise with user data
   */
  getUserById: async (userId) => {
    try {
      const response = await api.get(`/api/auth/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error(`Get user ${userId} error:`, error);
      throw error;
    }
  },

  /**
   * Admin: Create a new user
   * @param {Object} userData - User data (username, email, password, role)
   * @returns {Promise} - Promise with created user data
   */
  createUser: async (userData) => {
    try {
      // Transform the data to match backend's expected format
      const requestData = {
        ...userData,
        is_active: userData.isActive,  // Convert isActive to is_active
      };
      
      // Remove isActive to avoid duplicate/conflicting data
      delete requestData.isActive;
      
      const response = await api.post('/api/auth/users', requestData);
      return response.data;
    } catch (error) {
      console.error('Create user error:', error);
      throw error;
    }
  },

  /**
   * Admin: Update a user
   * @param {string} userId - User ID
   * @param {Object} userData - User data to update
   * @returns {Promise} - Promise with updated user data
   */
  updateUser: async (userId, userData) => {
    try {
      // Transform the data to match backend's expected format
      const requestData = {
        ...userData,
        // Only include is_active if isActive is defined in userData
        ...(userData.isActive !== undefined && { is_active: userData.isActive }),
      };
      
      // Remove isActive to avoid duplicate/conflicting data
      delete requestData.isActive;
      
      const response = await api.put(`/api/auth/users/${userId}`, requestData);
      return response.data;
    } catch (error) {
      console.error(`Update user ${userId} error:`, error);
      throw error;
    }
  },

  /**
   * Admin: Delete a user
   * @param {string} userId - User ID
   * @returns {Promise} - Promise with success message
   */
  deleteUser: async (userId) => {
    try {
      const response = await api.delete(`/api/auth/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error(`Delete user ${userId} error:`, error);
      throw error;
    }
  }
};

export default authService;
